/**
 * Funnel Editor Tour
 * Guides users through setting up their first funnel step with a design
 */

import type { Tour } from 'nextstepjs';
import { MousePointer2, Layers, Palette, Save, Eye } from 'lucide-react';

/**
 * Tour for first-time funnel setup
 * Triggered when a funnel is created with no design selected
 */
export const funnelEditorTour: Tour = {
  tour: 'funnel-editor-setup',
  steps: [
    {
      icon: <MousePointer2 className="h-5 w-5" />,
      title: 'Select Your Checkout Step',
      content: (
        <div className="space-y-2">
          <p>Click on this step to start customizing it.</p>
          <p className="text-muted-foreground text-sm">This will open the step editor on the right side.</p>
        </div>
      ),
      selector: '[data-tour="first-step"]',
      side: 'bottom',
      showControls: true,
      showSkip: true,
      pointerPadding: 15,
      pointerRadius: 12,
    },
    {
      icon: <Layers className="h-5 w-5" />,
      title: 'Choose a Template',
      content: (
        <div className="space-y-2">
          <p>Select a checkout template for your step.</p>
          <p className="text-muted-foreground text-sm">
            Templates provide pre-built page layouts you can customize.
          </p>
        </div>
      ),
      selector: '[data-tour="template-selector"]',
      side: 'left',
      showControls: true,
      showSkip: true,
      pointerPadding: 10,
      pointerRadius: 8,
    },
    {
      icon: <Palette className="h-5 w-5" />,
      title: 'Pick a Design',
      content: (
        <div className="space-y-2">
          <p>Choose a design variant for your checkout.</p>
          <p className="text-muted-foreground text-sm">
            Each design has different styles and layouts. You can always change it later.
          </p>
        </div>
      ),
      selector: '[data-tour="design-selector"]',
      side: 'left',
      showControls: true,
      showSkip: true,
      pointerPadding: 10,
      pointerRadius: 8,
    },
    {
      icon: <Save className="h-5 w-5" />,
      title: 'Save Your Changes',
      content: (
        <div className="space-y-2">
          <p>Click Save to save your funnel configuration.</p>
          <p className="text-muted-foreground text-sm">
            Your changes are saved to staging. You can promote to production when ready.
          </p>
        </div>
      ),
      selector: '[data-tour="save-button"]',
      side: 'top-right',
      showControls: true,
      showSkip: true,
      pointerPadding: 10,
      pointerRadius: 8,
    },
    {
      icon: <Eye className="h-5 w-5" />,
      title: 'Preview Your Checkout',
      content: (
        <div className="space-y-2">
          <p>Click the Preview button to see your checkout live.</p>
          <p className="text-muted-foreground text-sm">
            The preview opens in a new tab so you can see exactly what your customers will see.
          </p>
        </div>
      ),
      selector: '[data-tour="preview-button"]',
      side: 'top',
      showControls: true,
      showSkip: false,
      pointerPadding: 10,
      pointerRadius: 8,
    },
  ],
};

/**
 * Get funnel editor tour
 */
export function getFunnelEditorTour(): Tour[] {
  return [funnelEditorTour];
}
